/**
 * Every economy action the meta UI can take, as a named intent over the existing progression /
 * model functions. ONE table runs in both places (docs/design/multiplayer.md §4.2):
 *  - offline: ProfileStore runs the intent in the browser;
 *  - online: the browser predicts deterministic intents locally and the Worker (apps/server)
 *    runs the same entry on the stored profile with its own RNG seed; rolls wait for the server.
 *
 * Pure: no DOM, no storage, no clock (the caller passes the local date). Args arrive from the
 * network on the server, so `args` are coerced here (`str` / `int` / …) before use; everything
 * else (costs, caps, ownership) is progression's own validation.
 */
import type { BotSkillPreset, ClassId } from '@keystone/sim';
import { BOT_SKILL_PRESETS } from '@keystone/sim';
import {
  type ForgeRoll,
  type ItemSlot,
  type Profile,
  type ScaleGrade,
  type SecondaryStat,
  type TemperOpts,
  type ReportedKill,
  PROFILE_CLASSES,
  applyFieldKills,
  applyFieldPickups,
  type ReportedPickup,
  autoGemCharacter,
  bountyAccept,
  bountyTurnIn,
  catalyze,
  chooseSpec,
  claimVault,
  createCharacter,
  discardRefine,
  discoverArea,
  attuneWaystone,
  applyGather, // +prof
  questAbandon, // +quests
  questAccept, // +quests
  questTurnIn, // +quests
  dismissMigrationReport,
  drillSocket,
  dungeonClear,
  dungeonEnter,
  equipItem,
  fieldMove,
  keepRefine,
  masterworkItem,
  newProfile,
  refine,
  reforgeItem,
  salvage,
  sigilReshape,
  talkTo,
  temper,
  temperUntil,
  gridAllocate, // +grid
  gridAutoAllocate, // +grid
  gridKeyClaim, // +grid
  gridRefund, // +grid
  gridRespec, // +grid
  gridUnlock, // +grid
} from '@keystone/progression';
import { type CraftChoice, type CraftOutcome, type ForgeRecipe, craftBatch, craftOnce } from './screens/forgeModel';
import { autoEquipAll, fuseAll, salvageAll } from './screens/heroModel';
import { masterworkUntil } from './screens/temperModel';
import { type TalentEdit, applyRecommended, learnNode, resetTalents, unlearnNode } from './screens/talentsModel';
import { revertReforgeItem } from './screens/upgradeModel';

/** What an intent may read besides the profile. */
export interface IntentEnv {
  /** The player's local date 'YYYY-MM-DD' (the server clamps it to a real date somewhere). */
  localDate?: string;
  /**
   * Wall clock (ms) for the field kill budget and the dungeon clear floor (docs/design/zones.md).
   * Online the SERVER sets it from its own clock and never trusts the client's; offline the browser
   * passes Date.now(). Clocked intents fail without it.
   */
  nowMs?: number;
  /** Fresh profile seed (reset). The server's is secret; offline uses the browser's. */
  newSeed(): number;
}

export type IntentFail = { ok: false; reason: string };
export type IntentOk<X = unknown> = { ok: true; profile: Profile } & X;

export interface IntentDef<A, X> {
  /** True when the result depends on an RNG roll: online, the client must wait for the server. */
  readonly rolls: boolean;
  run(p: Profile, args: A, env: IntentEnv): IntentOk<X> | IntentFail;
}

const def = <A, X>(rolls: boolean, run: (p: Profile, a: A, env: IntentEnv) => IntentOk<X> | IntentFail): IntentDef<A, X> => ({ rolls, run });

// ---------------------------------------------------------------------------
// Arg coercion (args come off the wire on the server)
// ---------------------------------------------------------------------------

export class IntentArgError extends Error {}
const str = (v: unknown, what = 'arg'): string => {
  if (typeof v !== 'string' || v.length > 64) throw new IntentArgError(`bad ${what}`);
  return v;
};
const int = (v: unknown, lo: number, hi: number, what = 'arg'): number => {
  const n = typeof v === 'number' ? Math.trunc(v) : NaN;
  if (!Number.isFinite(n) || n < lo || n > hi) throw new IntentArgError(`bad ${what}`);
  return n;
};
const bool = (v: unknown): boolean => v === true;
const optInt = (v: unknown, lo: number, hi: number): number | undefined => (v === undefined || v === null ? undefined : int(v, lo, hi));
const strList = (v: unknown, max = 200): string[] => {
  if (!Array.isArray(v) || v.length > max) throw new IntentArgError('bad list');
  return v.map((x) => str(x, 'id'));
};
const classIdOf = (p: Profile, v: unknown): ClassId => {
  const c = str(v, 'classId');
  if (!p.characters[c]) throw new IntentArgError('unknown hero');
  return c;
};

function temperOpts(v: unknown): TemperOpts {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const out: TemperOpts = {};
  const venom = optInt(o['venom'], 0, 99);
  if (venom !== undefined) out.venom = venom;
  if (o['blessed'] !== undefined) out.blessed = bool(o['blessed']);
  if (o['useFree'] !== undefined) out.useFree = bool(o['useFree']);
  if (o['payer'] === 'wallet' || o['payer'] === 'satchel') out.payer = o['payer'];
  if (o['classId'] !== undefined) out.classId = str(o['classId'], 'classId');
  return out;
}

function recipeOf(v: unknown): ForgeRecipe {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const r: ForgeRecipe = {
    classId: str(o['classId'], 'classId'),
    slot: str(o['slot'], 'slot') as ItemSlot,
    grade: str(o['grade'], 'grade') as ScaleGrade,
    venom: int(o['venom'] ?? 0, 0, 99),
    moltskin: int(o['moltskin'] ?? 0, 0, 9),
    stats: (Array.isArray(o['stats']) ? o['stats'] : []).slice(0, 4).map((s) => str(s, 'stat') as SecondaryStat),
    sigil: bool(o['sigil']),
    eye: bool(o['eye']),
    blessed: bool(o['blessed']),
    token: o['token'] === null || o['token'] === undefined ? null : int(o['token'], 0, 99),
  };
  if (o['trinketEffectId'] !== undefined) r.trinketEffectId = str(o['trinketEffectId'], 'effect');
  return r;
}

function choiceOf(v: unknown): CraftChoice {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const c: CraftChoice = { slot: str(o['slot'], 'slot') as ItemSlot, venom: int(o['venom'] ?? 0, 0, 99) };
  if (o['grade'] !== undefined) c.grade = str(o['grade'], 'grade') as ScaleGrade;
  if (o['sigil'] !== undefined) c.sigil = bool(o['sigil']);
  if (o['eye'] !== undefined) c.eye = bool(o['eye']);
  return c;
}

/** progression OpResult / ForgeOpResult → intent outcome (the extra fields ride along). */
function op<R extends { ok: boolean }>(r: R): (R & { ok: true; profile: Profile }) | IntentFail {
  if (!r.ok) return { ok: false, reason: String((r as unknown as { reason: unknown }).reason) };
  return r as R & { ok: true; profile: Profile };
}
const talent = (r: TalentEdit): IntentOk<{ nodes: string[]; removed: string[] }> | IntentFail => (r.ok ? r : { ok: false, reason: r.reason });
const same = (p: Profile, next: Profile): IntentOk<{ changed: boolean }> => ({ ok: true, profile: next, changed: next !== p });

const BOT_PRESETS: readonly string[] = Object.keys(BOT_SKILL_PRESETS);

const now = (env: IntentEnv): number => {
  if (typeof env.nowMs !== 'number' || !Number.isFinite(env.nowMs)) throw new IntentArgError('no clock');
  return env.nowMs;
};
const num = (v: unknown, what = 'arg'): number => {
  if (typeof v !== 'number' || !Number.isFinite(v) || Math.abs(v) > 1e6) throw new IntentArgError(`bad ${what}`);
  return v;
};
function posOf(v: unknown): { x: number; y: number } | null {
  if (v === undefined || v === null) return null;
  const o = (typeof v === 'object' ? v : {}) as Record<string, unknown>;
  return { x: num(o['x'], 'pos'), y: num(o['y'], 'pos') };
}
/** Pickups off the wire: kind + amount (+ rarity for gear); progression clamps the rest. */
const pickupsOf = (v: unknown): ReportedPickup[] => {
  if (!Array.isArray(v) || v.length > 100) throw new IntentArgError('bad pickups');
  return v.map((x) => {
    const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
    const kind = o['kind'];
    if (kind !== 'gold' && kind !== 'xp' && kind !== 'item') throw new IntentArgError('bad pickup');
    return { kind, amount: int(o['amount'], 0, 1_000_000, 'amount'), ...(typeof o['rarity'] === 'string' ? { rarity: str(o['rarity'], 'rarity') } : {}) };
  });
};

/** Most kills one fieldKills report may carry (progression drops what is over its budget anyway). */
const MAX_KILLS = 200;
function killsOf(v: unknown): ReportedKill[] {
  if (!Array.isArray(v) || v.length > MAX_KILLS) throw new IntentArgError('bad kills');
  return v.map((k) => {
    const o = (k && typeof k === 'object' ? k : {}) as Record<string, unknown>;
    return { npcId: str(o['npcId'], 'npcId'), level: int(o['level'], 0, 999, 'level'), elite: bool(o['elite']) };
  });
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export const INTENTS = {
  // ---- settings / hero choice
  botPreset: def(false, (p, a: { preset: BotSkillPreset }) => {
    const preset = str(a.preset, 'preset');
    if (!BOT_PRESETS.includes(preset)) return { ok: false, reason: 'Unknown bot preset' };
    return same(p, p.settings.defaultBotPreset === preset ? p : { ...p, settings: { ...p.settings, defaultBotPreset: preset as BotSkillPreset } });
  }),
  /** One character per profile (save v5): the class is chosen once, at character creation. */
  mainClass: def(false, (_p, _a: { classId: ClassId }): IntentFail => ({ ok: false, reason: 'One character per profile' })),
  dismissMigration: def(false, (p) => same(p, dismissMigrationReport(p))),
  /** Start over: a fresh v5 profile with no character (`mainClassId` preselects the creation screen). */
  reset: def(true, (_p, a: { mainClassId: ClassId }, env): IntentOk | IntentFail => {
    const c = str(a.mainClassId, 'classId');
    if (!PROFILE_CLASSES.includes(c)) return { ok: false, reason: 'Unknown hero' };
    return { ok: true, profile: newProfile(c, env.newSeed() >>> 0) };
  }),

  // ---- the one character and the world (docs/design/zones.md Phase 1). Results carry FieldGain:
  //      { xp, gold, levels, level, completed: {kind, id, title, reward}[], bounties, chainAdvanced }.
  createCharacter: def(false, (p, a: { classId: ClassId; name: string; look?: number }) => op(createCharacter(p, str(a.classId, 'classId'), str(a.name, 'name'), optInt(a.look, 0, 99) ?? 0))),
  /** Client-reported kills: validated against the map's spawns, budgeted on the (server) clock. */
  /** Ground pickups (XP wisps, gold, gear drops) the sim says the hero collected; server-clock budget, items roll. */
  fieldPickups: def(true, (p, a: { mapId: string; pickups: ReportedPickup[] }, env) => op(applyFieldPickups(p, str(a.mapId, 'mapId'), pickupsOf(a.pickups), now(env)))),
  /** `auto`: the kills were made by Auto (same 100% XP; only advances the main quest's Auto step). */
  fieldKills: def(false, (p, a: { mapId: string; kills: ReportedKill[]; auto?: boolean }, env) => op(applyFieldKills(p, str(a.mapId, 'mapId'), killsOf(a.kills), now(env), undefined, bool(a.auto)))),
  fieldMove: def(false, (p, a: { mapId: string; pos?: { x: number; y: number } | null; facing?: number }) =>
    op(fieldMove(p, str(a.mapId, 'mapId'), posOf(a.pos), a.facing === undefined || a.facing === null ? 0 : num(a.facing, 'facing'))),
  ),
  /** +zone: first visit to a named area pays its discovery bonus once (and saves the position). */
  fieldDiscover: def(false, (p, a: { mapId: string; areaId: string; pos: { x: number; y: number } }) => {
    const pos = posOf(a.pos);
    if (!pos) return { ok: false, reason: 'No position' };
    return op(discoverArea(p, str(a.mapId, 'mapId'), str(a.areaId, 'areaId'), pos));
  }),
  /** +zone: walked up to a waystone: attune it (the waystone panel's destinations). */
  waystoneAttune: def(false, (p, a: { mapId: string; waystoneId: string; pos: { x: number; y: number } }) => {
    const pos = posOf(a.pos);
    if (!pos) return { ok: false, reason: 'No position' };
    return op(attuneWaystone(p, str(a.mapId, 'mapId'), str(a.waystoneId, 'waystoneId'), pos));
  }),
  /**
   * +prof: the hero finished gathering a node (the sim's 'gather' done). Checked against the map, the
   * hero's place and skill, the node's respawn floor and a server-clock budget; the skill-up and the
   * yield roll on the profile seed (server-side online). Result: FieldGain + `gather` (GatherResult).
   */
  fieldGather: def(true, (p, a: { mapId: string; nodeId: string; pos: { x: number; y: number } }, env) => {
    const pos = posOf(a.pos);
    if (!pos) return { ok: false, reason: 'No position' };
    return op(applyGather(p, str(a.mapId, 'mapId'), str(a.nodeId, 'nodeId'), pos, now(env)));
  }),
  bountyTalk: def(false, (p, a: { npcId: string }) => op(talkTo(p, str(a.npcId, 'npcId')))),
  /** +specs: take (or change, for RESPEC_GOLD) a specialisation at the Tower Warden (level 10+, in reach of her). */
  chooseSpec: def(false, (p, a: { specId: string }) => op(chooseSpec(p, str(a.specId, 'specId')))),
  bountyAccept: def(false, (p, a: { bountyId: string }) => op(bountyAccept(p, str(a.bountyId, 'bountyId')))),
  bountyTurnIn: def(false, (p, a: { bountyId: string }) => op(bountyTurnIn(p, str(a.bountyId, 'bountyId')))),
  /**
   * +quests: side quests (docs/design/zones.md "Side quests"). Accept / abandon are deterministic;
   * the turn-in rolls (a gear reward on the profile seed: the server's online). Progression clamps
   * them: the giver / turn-in NPC in reach of the saved position, level, chain order, the log size.
   * Kill progress rides on `fieldKills`.
   */
  questAccept: def(false, (p, a: { questId: string }) => op(questAccept(p, str(a.questId, 'questId')))),
  questAbandon: def(false, (p, a: { questId: string }) => op(questAbandon(p, str(a.questId, 'questId')))),
  questTurnIn: def(true, (p, a: { questId: string }) => op(questTurnIn(p, str(a.questId, 'questId')))),
  dungeonEnter: def(false, (p, a: { dungeonId: string }, env) => op(dungeonEnter(p, str(a.dungeonId, 'dungeonId'), now(env)))),
  /** First clear: a level of XP + a gear item + chest gold; refused < 120 s after dungeonEnter (server clock). */
  dungeonClear: def(true, (p, a: { dungeonId: string }, env) => op(dungeonClear(p, str(a.dungeonId, 'dungeonId'), now(env)))),

  // ---- gear
  equip: def(false, (p, a: { classId: ClassId; itemId: string }) => op(equipItem(p, classIdOf(p, a.classId), str(a.itemId, 'itemId')))),
  autoEquip: def(false, (p, a: { classId: ClassId }) => {
    const r = autoEquipAll(p, classIdOf(p, a.classId));
    return { ok: true, profile: r.profile, equipped: r.equipped };
  }),
  autoGem: def(false, (p, a: { classId: ClassId }) => op(autoGemCharacter(p, classIdOf(p, a.classId)))),
  fuseAll: def(false, (p) => {
    const r = fuseAll(p);
    return { ok: true, profile: r.profile, fused: r.fused };
  }),
  salvage: def(false, (p, a: { itemId: string }) => op(salvage(p, str(a.itemId, 'itemId')))),
  salvageAll: def(false, (p, a: { ids: string[] }) => {
    const r = salvageAll(p, strList(a.ids));
    return { ok: true, profile: r.profile, count: r.count, gained: r.gained };
  }),
  catalyze: def(false, (p, a: { itemId: string; classId: ClassId }) => op(catalyze(p, str(a.itemId, 'itemId'), classIdOf(p, a.classId)))),
  reforge: def(false, (p, a: { itemId: string; index: number; to: SecondaryStat }) => op(reforgeItem(p, str(a.itemId, 'itemId'), int(a.index, 0, 8), str(a.to, 'stat') as SecondaryStat))),
  revertReforge: def(false, (p, a: { itemId: string }) => op(revertReforgeItem(p, str(a.itemId, 'itemId')))),
  keepRefine: def(false, (p) => op(keepRefine(p))),
  discardRefine: def(false, (p) => op(discardRefine(p))),

  // ---- vault
  claimVault: def(false, (p, a: { index: number }) => {
    try {
      return { ok: true, profile: claimVault(p, int(a.index, 0, 9)) };
    } catch (err) {
      return { ok: false, reason: err instanceof Error ? err.message : String(err) };
    }
  }),

  // ---- talents
  talentLearn: def(false, (p, a: { classId: ClassId; id: string }) => talent(learnNode(p, classIdOf(p, a.classId), str(a.id, 'talent')))),
  talentUnlearn: def(false, (p, a: { classId: ClassId; id: string }) => talent(unlearnNode(p, classIdOf(p, a.classId), str(a.id, 'talent')))),
  talentReset: def(false, (p, a: { classId: ClassId }) => talent(resetTalents(p, classIdOf(p, a.classId)))),
  talentRecommended: def(false, (p, a: { classId: ClassId }) => talent(applyRecommended(p, classIdOf(p, a.classId)))),

  // ---- +grid: the Waystone Grid (docs/design/waystone-grid.md). Deterministic; progression checks
  //      adjacency, points, keys and gold. `gridAllocate` takes a whole planned path (all or nothing).
  gridAllocate: def(false, (p, a: { nodes: string[] }) => op(gridAllocate(p, strList(a.nodes, 80)))),
  gridRefund: def(false, (p, a: { nodeId: string }) => op(gridRefund(p, str(a.nodeId, 'nodeId')))),
  gridRespec: def(false, (p) => op(gridRespec(p))),
  gridUnlock: def(false, (p, a: { lockId: string }) => op(gridUnlock(p, str(a.lockId, 'lockId')))),
  gridAutoAllocate: def(false, (p) => op(gridAutoAllocate(p))),
  /** A key-bearing secret chest the hero opened (checked against the content and the reported position). */
  gridKeyClaim: def(false, (p, a: { mapId: string; secretId: string; pos: { x: number; y: number } }) => {
    const pos = posOf(a.pos);
    if (!pos) return { ok: false, reason: 'No position' };
    return op(gridKeyClaim(p, str(a.mapId, 'mapId'), str(a.secretId, 'secretId'), pos));
  }),

  // ---- rolls (forge RNG: server-side online)
  craft: def(true, (p, a: { recipe: ForgeRecipe }, env) => {
    const r = craftOnce(p, recipeOf(a.recipe), env.localDate);
    return r.ok ? { ok: true, profile: r.profile, out: r.out } : { ok: false, reason: r.reason };
  }),
  craftBatch: def(true, (p, a: { choice: CraftChoice; n: number }, env): IntentOk<{ results: CraftOutcome[]; stopped: 'epic' | 'blocked' | null }> => {
    const r = craftBatch(p, choiceOf(a.choice), int(a.n, 1, 10), env.localDate);
    return { ok: true, profile: r.profile, results: r.results, stopped: r.stopped };
  }),
  temper: def(true, (p, a: { itemId: string; opts: TemperOpts }) => op(temper(p, str(a.itemId, 'itemId'), temperOpts(a.opts)))),
  temperUntil: def(true, (p, a: { itemId: string; opts: TemperOpts; max: number }): IntentOk<{ rolls: ForgeRoll[]; blocked: string | null }> => {
    const r = temperUntil(p, str(a.itemId, 'itemId'), temperOpts(a.opts), int(a.max, 1, 10));
    return { ok: true, profile: r.profile, rolls: r.rolls, blocked: r.blocked };
  }),
  masterwork: def(true, (p, a: { itemId: string; opts: TemperOpts }) => op(masterworkItem(p, str(a.itemId, 'itemId'), temperOpts(a.opts)))),
  masterworkUntil: def(true, (p, a: { itemId: string; opts: TemperOpts; max: number }): IntentOk<{ rolls: ForgeRoll[]; blocked: string | null }> => {
    const r = masterworkUntil(p, str(a.itemId, 'itemId'), temperOpts(a.opts), int(a.max, 1, 10));
    return { ok: true, profile: r.profile, rolls: r.rolls, blocked: r.blocked };
  }),
  drill: def(true, (p, a: { itemId: string }) => op(drillSocket(p, str(a.itemId, 'itemId'), {}))),
  refine: def(true, (p, a: { itemId: string }) => op(refine(p, str(a.itemId, 'itemId')))),
  reshape: def(true, (p, a: { itemId: string }) => op(sigilReshape(p, str(a.itemId, 'itemId')))),
} as const;

export type IntentName = keyof typeof INTENTS;
export type IntentArgs<K extends IntentName> = Parameters<(typeof INTENTS)[K]['run']>[1];
export type IntentResult<K extends IntentName> = ReturnType<(typeof INTENTS)[K]['run']>;

export function isIntentName(name: unknown): name is IntentName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(INTENTS, name);
}

/** Run an intent; argument errors and throws become a failed outcome (never a crash). */
export function runIntent<K extends IntentName>(name: K, p: Profile, args: IntentArgs<K>, env: IntentEnv): IntentResult<K> {
  try {
    const d = INTENTS[name] as IntentDef<IntentArgs<K>, unknown>;
    return d.run(p, (args ?? {}) as IntentArgs<K>, env) as IntentResult<K>;
  } catch (err) {
    return { ok: false, reason: err instanceof IntentArgError ? err.message : `failed: ${err instanceof Error ? err.message : String(err)}` } as IntentResult<K>;
  }
}

/** The result fields besides the profile (what the server sends back next to the profile). */
export function intentExtras(r: { ok: boolean }): Record<string, unknown> {
  const { profile: _p, ...rest } = r as Record<string, unknown>;
  return rest;
}
